import { DateTime } from "luxon"
import { excelDateToISOString, formatStreet } from "../common/common.helpers"
import { MissionControlOrderDataBlob, MissionControlOrderItem } from "../common/common.interfaces"
import { EpicMappedOrderDetails, EpicOrderDetails, EpicOrderExcelRow, ProductQueryResult, ProductTransform } from "./epicOrders.interfaces"

export function mapEpicOrderItemsToOrder( 
  rawOrder: EpicOrderDetails,
  productDictionary: ProductQueryResult[]
): EpicMappedOrderDetails {
  const todayUtc = DateTime.utc().toISO()
  const EPIC_INT_ACCOUNT_ID = '0013l00002OBc1fAAD'
  const orderItems: MissionControlOrderItem[] = []
  const firstOrderRow = rawOrder.orderRows[0] as EpicOrderExcelRow;

  let errMsg = ""
  const REQUIRED_MSG = " is required to create an Order\n"
  for (const row of rawOrder.orderRows) {

    // #region PRODUCT LOOKUP
    /**
     * All product listing lookup is packed into a dictionary we can search instead of querying for N orders
     * This can fail if we can't find a matching listing for the item code provided in the XLS
     */
    const product = productDictionary.find(li => li.Listings__r?.records?.[0]?.SKU__c.toUpperCase() === row.ItemCode.toUpperCase())
    if (!product) {
      errMsg = errMsg.concat(`${row.ItemCode} did not match any Listing's Product in lookup\n`)
    }
    const requiredFields = [
      'CustomerPONo',
      'UnitCost',
      'QuantityOrdered'
    ]
    for (const field of requiredFields) {
      if (!row[field]) {
        errMsg = errMsg.concat(`${field}`).concat(REQUIRED_MSG)
        console.warn(errMsg.concat(`${JSON.stringify(row)}`))
      }
    }
    if(errMsg) { // don't throw here- wait until we validate the orders so we give a complete validation msg
      continue
    }
    
    // Happy path
    const productTransformed: ProductTransform = {
      Product2Id: product.Id,
      ListingId: product.Listings__r?.records?.[0]?.Id,
      ListingName: product.Listings__r?.records?.[0]?.Name,
      ListingSku: product.Listings__r?.records?.[0]?.SKU__c,
      PricebookEntryId: product.PricebookEntries?.records?.[0]?.Id
    }

    // #region MAP MC OBJECTS
    const orderItem: MissionControlOrderItem = {
      UnitPrice: row.UnitCost,
      Quantity: row.QuantityOrdered,
      PricebookEntryId: productTransformed.PricebookEntryId,
      Product2Id: productTransformed.Product2Id,
      Line_Item_Title__c: undefined,
      Listing__c: productTransformed.ListingId ? productTransformed.ListingId : undefined,
      External_Order_Line_ID__c: row.CustomerPONo,
      Fulfillment_Warehouse__c: undefined,
      Inventory__c: undefined
    }
    orderItems.push(orderItem)
  }

  /**
   * Order field validation for mapping to MC
   */
  const requiredFields = [
    'CustomerPONo',
    'ShipToName',
    'ShipToAddress1',
    'ShipToCity',
    'ShipToZipCode',
    'ShipToState'
  ]
  for (const field of requiredFields) {
    if (!firstOrderRow[field]) {
      errMsg = errMsg.concat(`${field}`).concat(REQUIRED_MSG)
    }
  }
  if (errMsg) { // if orderItems or order validation has failed by this point, exit
    console.warn(errMsg)
    throw new Error(errMsg)
  }

  const mappedOrder: MissionControlOrderDataBlob = {
    order: {
      Name: `${firstOrderRow.CustomerPONo}`,
      Unique_External_ID__c: firstOrderRow.CustomerPONo,
      AccountId: EPIC_INT_ACCOUNT_ID,
      Channel_Advisor_Shipping_Status__c: undefined,
      Order_Time__c: excelDateToISOString(firstOrderRow.RequiredDate),
      EffectiveDate: excelDateToISOString(firstOrderRow.RequiredDate),
      Customer_Name__c: undefined,
      Customer_Phone_Number__c: undefined,
      Estimated_Ship_Date__c: undefined,
      Order_Discount_Amount__c: 0,
      Order_Integration__c: undefined,
      Pricebook2Id: '01s1N0000066ylwQAA', /** make dynamic for the future if we start using multiple pricebooks */
      Shipping_Address_Name__c: firstOrderRow.ShipToName,
      Shipping_Carrier_Requested__c: undefined,
      Shipping_Class_Requested__c: undefined,
      Shipping_Collected__c: 0,
      ShippingCity: firstOrderRow.ShipToCity,
      ShippingCountry: 'US',
      BillingCountry: 'US',
      ShippingPostalCode: firstOrderRow.ShipToZipCode,
      ShippingState: firstOrderRow.ShipToState,
      ShippingStreet: formatStreet(firstOrderRow.ShipToAddress1, firstOrderRow.ShipToAddress2),
      Status: 'Draft',
      Tax_Collected__c: 0,
      Order_Tags__c: undefined
    },
    orderItems: orderItems,
  }
  
  return mappedOrder
  
}
